import React from "react";

import { formatEuro } from "../wizardCreateHelpers";
import WizardIcon from "./WizardIcon";

const MAX_REVIEW_PHOTOS = 6;

const EditButton = ({ t, onClick }) => (
  <button type="button" className="catalog-wizard-edit" onClick={onClick}>
    <WizardIcon name="edit" size={14} />
    {t("catalogProductStudio.step4Edit")}
  </button>
);

const CheckLine = ({ state, children }) => (
  <li>
    <span
      className={`catalog-wizard-status-icon${
        state === "blocked"
          ? " catalog-wizard-status-icon--blocked"
          : state === "warn"
          ? " catalog-wizard-status-icon--warn"
          : ""
      }`}
      aria-hidden="true"
    >
      {state === "ok" ? <WizardIcon name="check" size={11} strokeWidth={3.2} /> : "!"}
    </span>
    {children}
  </li>
);

const Step4Review = ({ t, summary, checklist, onEdit, getSwatch }) => {
  const dash = "—";
  const hard = new Set(checklist.hard);
  const soft = new Set(checklist.soft);
  const status = checklist.hard.length ? "blocked" : checklist.soft.length ? "warn" : "ok";
  const metaLine = [summary.categoryLabel, summary.brandName, summary.typeLabel].filter(Boolean).join(" · ");
  const visiblePhotos = summary.photos.slice(0, MAX_REVIEW_PHOTOS);
  const hiddenPhotoCount = summary.photos.length - visiblePhotos.length;

  const webshopStatus =
    summary.webshopFilledCount === 0
      ? t("catalogProductStudio.step4WebshopEmpty")
      : summary.webshopFilledCount === 1
      ? t("catalogProductStudio.step4WebshopFilledOne")
      : t("catalogProductStudio.step4WebshopFilled").replace("{count}", String(summary.webshopFilledCount));

  return (
    <>
      <section className="catalog-wizard-panel">
        <div className="catalog-wizard-card-head">
          <div>
            <h3>{t("catalogProductStudio.step4SummaryTitle")}</h3>
            <p>{t("catalogProductStudio.step4SummaryCopy")}</p>
          </div>
          <EditButton t={t} onClick={() => onEdit(1)} />
        </div>
        <div className="catalog-wizard-review-product">
          <div className="catalog-wizard-review-product__image">
            {summary.coverImageUrl ? (
              <img src={summary.coverImageUrl} alt={summary.title} />
            ) : (
              <span>{t("catalogProductStudio.previewNoImage")}</span>
            )}
          </div>
          <div>
            <div className="catalog-wizard-review-product__name">
              {summary.title || dash}
              <span className="catalog-wizard-badge">{t("catalogProductStudio.step4DraftBadge")}</span>
              {summary.metaCatalogEnabled ? (
                <span className="catalog-wizard-badge catalog-wizard-badge--info">
                  {t("catalogProductStudio.step4InCatalogBadge")}
                </span>
              ) : null}
            </div>
            {metaLine ? <div className="catalog-wizard-review-product__meta">{metaLine}</div> : null}
            {summary.description?.trim() ? (
              <p className="catalog-wizard-review-product__desc">{summary.description}</p>
            ) : null}
          </div>
          <dl className="catalog-wizard-kv">
            <div>
              <dt>{t("catalogProductStudio.step4Price")}</dt>
              <dd>{formatEuro(summary.price) || dash}</dd>
            </div>
            <div>
              <dt>{t("catalogProductStudio.step4ComparePrice")}</dt>
              <dd>{formatEuro(summary.compareAtPrice) || dash}</dd>
            </div>
            <div>
              <dt>{t("catalogProductStudio.step4Cost")}</dt>
              <dd>{formatEuro(summary.cost) || dash}</dd>
            </div>
            <div>
              <dt>{t("catalogProductStudio.step4Brand")}</dt>
              <dd>{summary.brandName || dash}</dd>
            </div>
            <div>
              <dt>{t("catalogProductStudio.step4Category")}</dt>
              <dd>{summary.categoryLabel || dash}</dd>
            </div>
            <div>
              <dt>{t("catalogProductStudio.step4Type")}</dt>
              <dd>{summary.typeLabel || dash}</dd>
            </div>
          </dl>
        </div>
      </section>

      <section className="catalog-wizard-panel">
        <div className="catalog-wizard-card-head">
          <div>
            <h3>
              {t("catalogProductStudio.step4VariantsCard")}
              <span className="catalog-wizard-card-head__count">
                {summary.variantCount === 1
                  ? t("catalogProductStudio.variantsCountOne")
                  : t("catalogProductStudio.variantsCount").replace("{count}", String(summary.variantCount))}
              </span>
            </h3>
          </div>
          <EditButton t={t} onClick={() => onEdit(2)} />
        </div>
        <div className="catalog-wizard-review-colors">
          {summary.colorSummaries.map((entry) => (
            <div key={entry.color || "__default__"} className="catalog-wizard-review-color">
              {entry.color ? (
                <span
                  className="catalog-wizard-swatch catalog-wizard-swatch--xl"
                  style={{ "--catalog-color-swatch-value": getSwatch(entry.color) }}
                  aria-hidden="true"
                />
              ) : null}
              <div className="catalog-wizard-review-color__body">
                <strong>{entry.color || summary.title || dash}</strong>
                <span>
                  {entry.variantCount === 1
                    ? t("catalogProductStudio.variantsCountOne")
                    : t("catalogProductStudio.variantsCount").replace("{count}", String(entry.variantCount))}
                  {" · "}
                  {t("catalogProductStudio.step4StockTotal").replace("{count}", String(entry.stock))}
                </span>
                {entry.sizes.length ? (
                  <div className="catalog-wizard-tile__dots">
                    {entry.sizes.map((size) => (
                      <span key={size} className="catalog-wizard-mini-chip">
                        {size}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
              {entry.coverUrl ? (
                <img className="catalog-wizard-review-color__thumb" src={entry.coverUrl} alt={entry.color || ""} />
              ) : null}
            </div>
          ))}
        </div>
      </section>

      <section className="catalog-wizard-panel">
        <div className="catalog-wizard-card-head">
          <div>
            <h3>
              {t("catalogProductStudio.step4PhotosCard")}
              <span className="catalog-wizard-card-head__count">
                {t("catalogProductStudio.step4PhotosTotal").replace("{count}", String(summary.photos.length))}
              </span>
            </h3>
          </div>
          <EditButton t={t} onClick={() => onEdit(3)} />
        </div>
        {visiblePhotos.length ? (
          <div className="catalog-wizard-review-photos">
            {visiblePhotos.map((url, index) => (
              <img key={`${url}-${index}`} src={url} alt="" />
            ))}
            {hiddenPhotoCount > 0 ? (
              <div className="catalog-wizard-review-photos__more">
                +{hiddenPhotoCount}
                <span>{t("catalogProductStudio.step4MorePhotos")}</span>
              </div>
            ) : null}
          </div>
        ) : (
          <p className="catalog-wizard-empty mb-0">{t("catalogProductStudio.step4NoPhotos")}</p>
        )}
      </section>

      <div className="catalog-wizard-review-pair">
        <section className="catalog-wizard-panel">
          <div className="catalog-wizard-feature">
            <span className="catalog-wizard-feature__icon">
              <WizardIcon name="share" size={20} />
            </span>
            <div className="catalog-wizard-feature__body">
              <strong>Meta Catalog</strong>
              <span className={`catalog-wizard-feature__status${summary.metaCatalogEnabled ? "" : " is-muted"}`}>
                {summary.metaCatalogEnabled
                  ? t("catalogProductStudio.step4MetaIncluded")
                  : t("catalogProductStudio.step4MetaExcluded")}
              </span>
              <p>
                {summary.metaCatalogEnabled
                  ? t("catalogProductStudio.step4MetaIncludedCopy")
                  : t("catalogProductStudio.step4MetaExcludedCopy")}
              </p>
            </div>
            <EditButton t={t} onClick={() => onEdit(1)} />
          </div>
        </section>
        <section className="catalog-wizard-panel">
          <div className="catalog-wizard-feature">
            <span className="catalog-wizard-feature__icon">
              <WizardIcon name="list" size={20} />
            </span>
            <div className="catalog-wizard-feature__body">
              <strong>{t("catalogProductStudio.step1WebshopDetailsSection")}</strong>
              <span className={`catalog-wizard-feature__status${summary.webshopFilledCount ? "" : " is-muted"}`}>
                {webshopStatus}
              </span>
              <p>{t("catalogProductStudio.step4WebshopCopy")}</p>
            </div>
            <EditButton t={t} onClick={() => onEdit(1)} />
          </div>
        </section>
      </div>

      <section className="catalog-wizard-panel" data-testid="wizard-checks">
        <div className="catalog-wizard-checks">
          <div className="catalog-wizard-checks__summary">
            <span
              className={`catalog-wizard-status-icon catalog-wizard-status-icon--lg${
                status === "blocked"
                  ? " catalog-wizard-status-icon--blocked"
                  : status === "warn"
                  ? " catalog-wizard-status-icon--warn"
                  : ""
              }`}
              aria-hidden="true"
            >
              {status === "ok" ? <WizardIcon name="check" size={22} strokeWidth={3} /> : "!"}
            </span>
            <div>
              <strong>{t("catalogProductStudio.step4ChecksTitle")}</strong>
              <span>
                {status === "blocked"
                  ? t("catalogProductStudio.step4ChecksBlocked")
                  : status === "warn"
                  ? t("catalogProductStudio.step4ChecksWarn")
                  : t("catalogProductStudio.step4ChecksOk")}
              </span>
            </div>
          </div>
          <ul className="catalog-wizard-checklist">
            {hard.has("name") ? (
              <CheckLine state="blocked">{t("catalogProductStudio.step4CheckNameMissing")}</CheckLine>
            ) : null}
            {hard.has("price") ? (
              <CheckLine state="blocked">{t("catalogProductStudio.step4CheckPriceMissing")}</CheckLine>
            ) : null}
            {!hard.has("name") && !hard.has("price") ? (
              <CheckLine state="ok">{t("catalogProductStudio.step4CheckBasicOk")}</CheckLine>
            ) : null}
            {hard.has("variants") ? (
              <CheckLine state="blocked">{t("catalogProductStudio.step4CheckVariantsTooMany")}</CheckLine>
            ) : (
              <CheckLine state="ok">{t("catalogProductStudio.step4CheckVariantsOk")}</CheckLine>
            )}
            <CheckLine state="ok">{t("catalogProductStudio.step4CheckSkuReady")}</CheckLine>
            {soft.has("noImages") ? (
              <CheckLine state="warn">{t("catalogProductStudio.step4CheckNoPhotos")}</CheckLine>
            ) : (
              <CheckLine state="ok">{t("catalogProductStudio.step4CheckPhotosOk")}</CheckLine>
            )}
            {soft.has("zeroStock") ? (
              <CheckLine state="warn">{t("catalogProductStudio.step4CheckZeroStock")}</CheckLine>
            ) : (
              <CheckLine state="ok">{t("catalogProductStudio.step4CheckStockOk")}</CheckLine>
            )}
            {summary.colors.length > 1 ? (
              soft.has("missingColorCover") ? (
                <CheckLine state="warn">{t("catalogProductStudio.step4CheckMissingColorCover")}</CheckLine>
              ) : (
                <CheckLine state="ok">{t("catalogProductStudio.step4CheckColorCoversOk")}</CheckLine>
              )
            ) : null}
            {soft.has("incompleteWebshop") ? (
              <CheckLine state="warn">{t("catalogProductStudio.step4CheckIncompleteWebshop")}</CheckLine>
            ) : (
              <CheckLine state="ok">{t("catalogProductStudio.step4CheckWebshopOk")}</CheckLine>
            )}
          </ul>
        </div>
      </section>
    </>
  );
};

export default Step4Review;
